const DEFAULT_TRANSLATION_MAP = {
  NO_FACE_DETECTED: 'Position yourself so that your face is in the center of the frame until your phone vibrates.',
  BAD_ANGLE: 'Please turn your face slightly toward the camera.',
  FACE_TOO_FAR: 'Move closer to the camera.',
  FACE_TOO_CLOSE: 'Move back slightly from the camera.',
  LOW_LIGHT: 'Please move to a brighter area and try again.',
  LIVENESS_OK: 'Perfect. You are ready to capture.'
};
const shouldAutoCapture = (status, enabled) => enabled && status === 'LIVENESS_OK';
const WHITE_GUIDE_ARCS = `
  <svg class="vc-guide-arc vc-guide-arc-top" xmlns="http://www.w3.org/2000/svg" width="171" height="28" viewBox="0 0 171 28" fill="none"><path d="M2 25.7526C26.0947 10.6994 54.5669 2 85.071 2C115.575 2 144.047 10.6994 168.142 25.7526" stroke="white" stroke-width="4" stroke-linecap="round" /></svg>
  <svg class="vc-guide-arc vc-guide-arc-right" xmlns="http://www.w3.org/2000/svg" width="28" height="171" viewBox="0 0 28 171" fill="none"><path d="M2.00022 168.143C17.0534 144.048 25.7528 115.576 25.7528 85.0718C25.7528 54.5676 17.0533 26.0953 2 2.00049" stroke="white" stroke-width="4" stroke-linecap="round" /></svg>
  <svg class="vc-guide-arc vc-guide-arc-bottom" xmlns="http://www.w3.org/2000/svg" width="171" height="28" viewBox="0 0 171 28" fill="none"><path d="M168.143 2.00052C144.048 17.0538 115.576 25.7533 85.0714 25.7533C54.5671 25.7533 26.0948 17.0538 2 2.00049" stroke="white" stroke-width="4" stroke-linecap="round" /></svg>
  <svg class="vc-guide-arc vc-guide-arc-left" xmlns="http://www.w3.org/2000/svg" width="28" height="171" viewBox="0 0 28 171" fill="none"><path d="M25.7528 2.00049C10.6995 26.0953 2 54.5676 2 85.0718C2 115.576 10.6994 144.048 25.7526 168.143" stroke="white" stroke-width="4" stroke-linecap="round" /></svg>
`;
const GREEN_GUIDE_ARCS = `
  <svg class="vc-guide-arc vc-guide-arc-top" xmlns="http://www.w3.org/2000/svg" width="174" height="32" viewBox="0 0 174 32" fill="none"><path d="M4 27.4037C28.0417 12.5716 56.4512 4 86.8882 4C117.325 4 145.735 12.5716 169.776 27.4037" stroke="#2BBC86" stroke-width="8" stroke-linecap="round" /></svg>
  <svg class="vc-guide-arc vc-guide-arc-right" xmlns="http://www.w3.org/2000/svg" width="32" height="174" viewBox="0 0 32 174" fill="none"><path d="M4.00021 169.777C18.8321 145.735 27.4037 117.326 27.4037 86.8888C27.4037 56.4518 18.8321 28.0422 4 4.00049" stroke="#2BBC86" stroke-width="8" stroke-linecap="round" /></svg>
  <svg class="vc-guide-arc vc-guide-arc-bottom" xmlns="http://www.w3.org/2000/svg" width="174" height="32" viewBox="0 0 174 32" fill="none"><path d="M169.776 4.00051C145.735 18.8326 117.325 27.4042 86.8882 27.4042C56.4512 27.4042 28.0417 18.8326 4 4.00049" stroke="#2BBC86" stroke-width="8" stroke-linecap="round" /></svg>
  <svg class="vc-guide-arc vc-guide-arc-left" xmlns="http://www.w3.org/2000/svg" width="32" height="174" viewBox="0 0 32 174" fill="none"><path d="M27.4037 4.00049C12.5716 28.0422 4 56.4518 4 86.8888C4 117.326 12.5715 145.735 27.4035 169.777" stroke="#2BBC86" stroke-width="8" stroke-linecap="round" /></svg>
`;
const INVALID_LIVENESS_CODES = new Set(['NO_FACE_DETECTED', 'BAD_ANGLE', 'FACE_TOO_FAR', 'FACE_TOO_CLOSE', 'LOW_LIGHT']);
const getTranslationMap = instance => {
  try {
    const raw = instance.getAttribute('translation_map') || '{}';
    return {
      ...DEFAULT_TRANSLATION_MAP,
      ...JSON.parse(raw)
    };
  } catch (error) {
    return {
      ...DEFAULT_TRANSLATION_MAP
    };
  }
};
const dispatchCustomEvent = (element, eventName, detail) => {
  element.dispatchEvent(new CustomEvent(eventName, {
    detail
  }));
};
const HTMLElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;
class VerifoneFaceCapture extends HTMLElementBase {
  static get observedAttributes() {
    return ['init_button_text', 'open_button_text', 'button_styles', 'message_styles', 'disable_on_invalid', 'show_guides', 'translation_map'];
  }
  constructor() {
    super();
    this.attachShadow({
      mode: 'open'
    });
    this.stream = null;
    this.video = null;
    this.button = null;
    this.flashButton = null;
    this.refreshButton = null;
    this.message = null;
    this.livenessTimer = null;
    this.currentStatus = 'NO_FACE_DETECTED';
    this.statusIndex = 0;
    this.ready = false;
    this.autoCaptureTriggered = false;
    this.capturing = false;
    this.refreshing = false;
    this.flashEnabled = false;
    this.captureTransitionTimer = null;
    this.statusCycle = ['NO_FACE_DETECTED', 'BAD_ANGLE', 'FACE_TOO_FAR', 'LOW_LIGHT', 'LIVENESS_OK', 'FACE_TOO_CLOSE'];
  }
  connectedCallback() {
    this.render();
    this.bindEvents();
    this.initialize();
  }
  disconnectedCallback() {
    this.stopCamera();
    if (this.livenessTimer) {
      clearInterval(this.livenessTimer);
      this.livenessTimer = null;
    }
    if (this.captureTransitionTimer) {
      clearTimeout(this.captureTransitionTimer);
      this.captureTransitionTimer = null;
    }
  }
  attributeChangedCallback() {
    this.render();
  }
  getButtonText() {
    return this.ready ? this.getAttribute('open_button_text') || 'Take Photo' : this.getAttribute('init_button_text') || 'Pay With A Smile';
  }
  getMessage(code) {
    return getTranslationMap(this)[code] || DEFAULT_TRANSLATION_MAP.NO_FACE_DETECTED;
  }
  syncButtonState() {
    if (!this.button) {
      return;
    }
    const isInvalid = this.hasAttribute('disable_on_invalid') && INVALID_LIVENESS_CODES.has(this.currentStatus);
    this.button.disabled = !this.ready || isInvalid;
    this.button.textContent = this.getButtonText();
  }
  render() {
    const showGuides = this.hasAttribute('show_guides');
    const buttonStyles = this.getAttribute('button_styles') || '';
    const messageStyles = this.getAttribute('message_styles') || '';
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
          min-width: 0;
          max-width: 100%;
        }
        .vc-shell {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: 0;
        }
        .vc-preview {
          position: relative;
          width: min(402px, 100%);
          height: min(402px, 100vw);
          max-width: 402px;
          max-height: 402px;
          margin: 0 auto;
          aspect-ratio: 1 / 1;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 0;
          overflow: hidden;
        }
        .vc-preview video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
        }
        .vc-message {
          min-height: 158px;
          margin: 0;
          padding: 16px;
          text-align: left;
          color: #3d3d3d;
          ${messageStyles}
        }
        .vc-message h2 {
          margin: 0 0 4px;
          color: #3d3d3d;
          font-feature-settings: 'liga' off, 'clig' off;
          font-family: BentonSans, Arial, sans-serif;
          font-size: 24px;
          font-style: normal;
          font-weight: 700;
          line-height: 32px;
          letter-spacing: 0;
        }
        .vc-message p {
          margin: 0;
          color: #3d3d3d;
          font-family: 'SF Pro', BentonSans, Arial, sans-serif;
          font-size: 16px;
          font-style: normal;
          font-weight: 400;
          line-height: 24px;
          letter-spacing: 0;
        }
        .vc-auto-capture {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          margin-top: 28px;
          color: #313131;
          text-align: center;
          font-family: 'ABC Monument Grotesk', BentonSans, Arial, sans-serif;
          font-size: 12px;
          font-style: normal;
          font-weight: 400;
          line-height: 18px;
        }
        .vc-auto-capture svg {
          width: 12px;
          height: 12px;
          display: block;
        }
        .vc-controls {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin: 28px 40px 0;
        }
        .vc-control {
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          border: 0;
          background: transparent;
          color: #006fcf;
        }
        .vc-control:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .vc-flash-control[aria-pressed="true"] {
          border-radius: 50%;
          background: #eaf5ff;
        }
        .vc-control svg {
          width: 24px;
          height: 24px;
          display: block;
        }
        .vc-guide {
          position: absolute;
          top: 38px;
          right: 42px;
          bottom: 42px;
          left: 38px;
          box-sizing: border-box;
          border-radius: 50%;
          pointer-events: none;
        }
        .vc-guide-arcs {
          position: absolute;
          inset: 0;
          opacity: 1;
        }
        .vc-guide-success-arcs {
          opacity: 0;
          transition: opacity 180ms ease-out;
        }
        .vc-guide.success .vc-guide-success-arcs {
          opacity: 1;
        }
        .vc-guide-arc {
          position: absolute;
          display: block;
          overflow: visible;
        }
        .vc-guide-arc-top,
        .vc-guide-arc-bottom {
          width: 54.1%;
          height: auto;
          left: 50%;
          transform: translateX(-50%);
        }
        .vc-guide-arc-top {
          top: 0;
        }
        .vc-guide-arc-bottom {
          bottom: 0;
        }
        .vc-guide-arc-left,
        .vc-guide-arc-right {
          width: auto;
          height: 54.1%;
          top: 50%;
          transform: translateY(-50%);
        }
        .vc-guide-arc-left {
          left: 0;
        }
        .vc-guide-arc-right {
          right: 0;
        }
        .vc-guide-success-arcs .vc-guide-arc-top,
        .vc-guide-success-arcs .vc-guide-arc-bottom {
          width: 55.1%;
        }
        .vc-guide-success-arcs .vc-guide-arc-left,
        .vc-guide-success-arcs .vc-guide-arc-right {
          height: 55.1%;
        }
      </style>
      <div class="vc-shell">
        <div class="vc-preview">
          <video autoplay playsinline muted></video>
          ${showGuides ? `<div class="vc-guide"><div class="vc-guide-arcs vc-guide-white-arcs">${WHITE_GUIDE_ARCS}</div><div class="vc-guide-arcs vc-guide-success-arcs">${GREEN_GUIDE_ARCS}</div></div>` : ''}
        </div>
        <div class="vc-message">
          <h2>Center your face</h2>
          <p class="vc-message-copy">${this.getMessage(this.currentStatus)}</p>
          <div class="vc-auto-capture">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M10.75 4C10.75 3.86008 10.6964 3.72987 10.6084 3.6377C10.5213 3.5467 10.4084 3.50012 10.2959 3.5H8.38672C8.21586 3.5 8.05664 3.41265 7.96484 3.26855L7.15723 2H4.84277L4.03516 3.26855C3.94336 3.41265 3.78414 3.5 3.61328 3.5H1.7041C1.59156 3.50012 1.47866 3.5467 1.3916 3.6377C1.30362 3.72987 1.25 3.86008 1.25 4V9.5C1.25 9.63992 1.30362 9.77013 1.3916 9.8623C1.47866 9.9533 1.59156 9.99988 1.7041 10H10.2959C10.4084 9.99988 10.5213 9.9533 10.6084 9.8623C10.6964 9.77013 10.75 9.63992 10.75 9.5V4ZM7.375 6.375C7.375 5.61561 6.75939 5 6 5C5.24061 5 4.625 5.61561 4.625 6.375C4.625 7.13439 5.24061 7.75 6 7.75C6.75939 7.75 7.375 7.13439 7.375 6.375ZM8.375 6.375C8.375 7.68668 7.31168 8.75 6 8.75C4.68832 8.75 3.625 7.68668 3.625 6.375C3.625 5.06332 4.68832 4 6 4C7.31168 4 8.375 5.06332 8.375 6.375ZM11.75 9.5C11.75 9.89051 11.6021 10.2698 11.332 10.5527C11.0614 10.8362 10.6891 10.9999 10.2959 11H1.7041C1.3109 10.9999 0.938643 10.8362 0.667969 10.5527C0.397928 10.2698 0.25 9.89051 0.25 9.5V4C0.25 3.60949 0.397928 3.23017 0.667969 2.94727C0.938643 2.66383 1.3109 2.50012 1.7041 2.5H3.33887L4.14648 1.23145L4.18359 1.18066C4.27787 1.0671 4.41861 1 4.56836 1H7.43164L7.49512 1.00391C7.64136 1.02255 7.77317 1.10528 7.85352 1.23145L8.66113 2.5H10.2959C10.6891 2.50012 11.0614 2.66383 11.332 2.94727C11.6021 3.23016 11.75 3.60949 11.75 4V9.5Z" fill="#313131" /></svg>
            <span>Auto capture on</span>
          </div>
          <div class="vc-controls">
            <button type="button" class="vc-control vc-flash-control" aria-label="Turn flash on" aria-pressed="false"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M13.9384 1.94494C14.9875 0.896181 16.7596 1.84499 16.4687 3.29943L15.3281 9.00158H19.497C20.8329 9.00158 21.5022 10.6167 20.5575 11.5611L10.0605 22.0553C9.01135 23.1034 7.23932 22.155 7.5302 20.7008L8.67083 14.9986H4.50188C3.16616 14.9984 2.49775 13.3834 3.44231 12.4391L13.9384 1.94494ZM4.50188 13.4986H10.4999L9.00091 20.9947L19.497 10.5006H13.499L14.999 3.00451L4.50188 13.4986Z" fill="#0066BE" /></svg></button>
            <button type="button" class="vc-control" aria-label="Refresh camera"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path fillRule="evenodd" clipRule="evenodd" d="M12.2145 0.752102L12 0.75L11.814 0.751507C5.68656 0.850854 0.75 5.84893 0.75 12V12.5L0.753433 12.5722C0.789776 12.9526 1.11015 13.25 1.5 13.25C1.91421 13.25 2.25 12.9142 2.25 12.5V12L2.25148 11.8285C2.34201 6.57989 6.57989 2.34201 11.8285 2.25148L12 2.25L12.2093 2.25232C14.7875 2.30953 17.2333 3.42158 18.9721 5.33423L19.8045 6.25H18.5L18.4278 6.25343C18.0474 6.28978 17.75 6.61015 17.75 7C17.75 7.41421 18.0858 7.75 18.5 7.75H21.5L21.5363 7.74914L21.6089 7.74213L21.6637 7.73206L21.7374 7.71163L21.798 7.68847L21.8617 7.65723L21.9226 7.6197L21.9787 7.57739L22.0045 7.55496L22.0507 7.50918L22.092 7.46054L22.1411 7.38961L22.1768 7.32362L22.2051 7.25611L22.2331 7.15924L22.242 7.10913L22.2492 7.03548L22.25 4L22.2466 3.92777C22.2102 3.54744 21.8898 3.25 21.5 3.25L21.4278 3.25343C21.0474 3.28978 20.75 3.61015 20.75 4V5.06L20.082 4.32523L19.9326 4.16416C17.9211 2.03892 15.1402 0.809475 12.2145 0.752102ZM23.2466 11.4278C23.2102 11.0474 22.8898 10.75 22.5 10.75C22.0858 10.75 21.75 11.0858 21.75 11.5V12L21.7485 12.1715C21.657 17.4772 17.3275 21.75 12 21.75L11.7952 21.7479C8.93128 21.6881 6.23252 20.3776 4.41428 18.1553L4.0825 17.75H5.5L5.57223 17.7466C5.95256 17.7102 6.25 17.3898 6.25 17C6.25 16.5858 5.91421 16.25 5.5 16.25L2.47974 16.2502L2.44772 16.2518L2.38155 16.2593L2.28737 16.2806C2.2415 16.2941 2.19745 16.3119 2.15563 16.3336L2.14171 16.341L2.08242 16.3769L2.03144 16.4144L1.99078 16.4493L1.94387 16.4968L1.89998 16.5499L1.86644 16.5984L1.83244 16.6578C1.77974 16.7604 1.75 16.8767 1.75 17V20L1.75343 20.0722C1.78978 20.4526 2.11015 20.75 2.5 20.75L2.57223 20.7466C2.95256 20.7102 3.25 20.3898 3.25 20V19.1015L3.39179 19.2711C5.48985 21.7378 8.5462 23.1861 11.7839 23.2479L12 23.25L12.186 23.2485C18.3134 23.1491 23.25 18.1511 23.25 12V11.5L23.2466 11.4278Z" fill="#0066BE" /></svg></button>
          </div>
        </div>
      </div>
    `;
    this.video = this.shadowRoot.querySelector('video');
    this.button = this.shadowRoot.querySelector('.vc-button');
    this.flashButton = this.shadowRoot.querySelector('.vc-flash-control');
    this.refreshButton = this.shadowRoot.querySelector('[aria-label="Refresh camera"]');
    this.message = this.shadowRoot.querySelector('.vc-message-copy');
    this.preview = this.shadowRoot.querySelector('.vc-preview');
    this.bindEvents();
    this.syncButtonState();
  }
  bindEvents() {
    if (this.flashButton) {
      this.flashButton.onclick = () => this.toggleFlash();
    }
    if (this.refreshButton) {
      this.refreshButton.onclick = () => this.refreshCamera();
    }
    if (!this.button) {
      return;
    }
    this.button.onclick = async () => {
      if (!this.ready) {
        await this.initialize();
        return;
      }
      await this.capture();
    };
  }
  setStatus(code) {
    this.currentStatus = code;
    const valid = !INVALID_LIVENESS_CODES.has(code);
    if (!valid) {
      this.autoCaptureTriggered = false;
    }
    const map = getTranslationMap(this);
    const message = map[code] || DEFAULT_TRANSLATION_MAP.NO_FACE_DETECTED;
    console.log('[verifone-face-capture] status:', code, '| message:', message);
    if (this.message) {
      this.message.textContent = message;
    }
    if (this.preview) {
      var _this$preview$querySe;
      (_this$preview$querySe = this.preview.querySelector('.vc-guide')) === null || _this$preview$querySe === void 0 || _this$preview$querySe.classList.toggle('success', code === 'LIVENESS_OK');
    }
    if (this.button) {
      this.syncButtonState();
    }
    dispatchCustomEvent(this, 'webCapFaceDetection', {
      code,
      message,
      status: valid ? 'VALID' : 'INVALID'
    });
    if (shouldAutoCapture(code, this.ready) && !this.autoCaptureTriggered && !this.capturing) {
      this.autoCaptureTriggered = true;
      console.log('[verifone-face-capture] auto-capture queued for valid state:', code);
      if (this.livenessTimer) {
        clearInterval(this.livenessTimer);
        this.livenessTimer = null;
      }
      window.setTimeout(() => {
        var _this$button;
        if (this.ready && !((_this$button = this.button) !== null && _this$button !== void 0 && _this$button.disabled) && !INVALID_LIVENESS_CODES.has(this.currentStatus) && !this.capturing) {
          console.log('[verifone-face-capture] executing auto-capture with status:', this.currentStatus);
          this.capture();
        }
      }, 150);
    }
  }
  startLivenessCycle() {
    if (this.livenessTimer) {
      clearInterval(this.livenessTimer);
    }
    this.setStatus('NO_FACE_DETECTED');
    this.livenessTimer = setInterval(() => {
      const nextStatus = this.statusCycle[(this.statusIndex + 1) % this.statusCycle.length];
      this.statusIndex += 1;
      this.setStatus(nextStatus);
    }, 2500);
  }
  getVideoTrack() {
    var _this$stream, _this$stream$getVideo, _this$stream2, _this$stream2$getTrac;
    return ((_this$stream = this.stream) === null || _this$stream === void 0 || (_this$stream$getVideo = _this$stream.getVideoTracks) === null || _this$stream$getVideo === void 0 ? void 0 : _this$stream$getVideo.call(_this$stream)[0]) || ((_this$stream2 = this.stream) === null || _this$stream2 === void 0 || (_this$stream2$getTrac = _this$stream2.getTracks) === null || _this$stream2$getTrac === void 0 ? void 0 : _this$stream2$getTrac.call(_this$stream2).find(track => track.kind === 'video')) || null;
  }
  syncFlashButton() {
    if (!this.flashButton) return;
    const track = this.getVideoTrack();
    let supportsTorch = false;
    try {
      var _track$getCapabilitie;
      supportsTorch = Boolean((track === null || track === void 0 || (_track$getCapabilitie = track.getCapabilities) === null || _track$getCapabilitie === void 0 ? void 0 : _track$getCapabilitie.call(track).torch) && typeof track.applyConstraints === 'function');
    } catch {
      supportsTorch = false;
    }
    this.flashButton.disabled = this.refreshing;
    this.flashButton.title = supportsTorch ? 'Toggle camera flash' : 'Flash is not supported by this camera';
    this.flashButton.setAttribute('aria-pressed', String(this.flashEnabled));
    this.flashButton.setAttribute('aria-label', this.flashEnabled ? 'Turn flash off' : 'Turn flash on');
  }
  async toggleFlash() {
    console.log('[verifone-face-capture] Flash toggle requested');
    const track = this.getVideoTrack();
    if (!this.ready || !track || this.refreshing) {
      console.log('[verifone-face-capture] Flash toggle skipped; camera is not ready');
      return;
    }
    let supportsTorch = false;
    try {
      var _track$getCapabilitie2;
      supportsTorch = Boolean(((_track$getCapabilitie2 = track.getCapabilities) === null || _track$getCapabilitie2 === void 0 ? void 0 : _track$getCapabilitie2.call(track).torch) && typeof track.applyConstraints === 'function');
    } catch {
      supportsTorch = false;
    }
    if (!supportsTorch) {
      console.log('[verifone-face-capture] Flash is not supported by this camera');
      return;
    }
    const nextFlashEnabled = !this.flashEnabled;
    try {
      await track.applyConstraints({
        advanced: [{
          torch: nextFlashEnabled
        }]
      });
      this.flashEnabled = nextFlashEnabled;
      this.syncFlashButton();
      console.log(`[verifone-face-capture] Flash ${nextFlashEnabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.log('[verifone-face-capture] Flash toggle failed', error);
      dispatchCustomEvent(this, 'webCapError', {
        message: 'Unable to toggle camera flash',
        error
      });
    }
  }
  async refreshCamera() {
    if (this.refreshing || this.capturing) return;
    this.refreshing = true;
    if (this.refreshButton) this.refreshButton.disabled = true;
    if (this.livenessTimer) {
      clearInterval(this.livenessTimer);
      this.livenessTimer = null;
    }
    if (this.captureTransitionTimer) {
      clearTimeout(this.captureTransitionTimer);
      this.captureTransitionTimer = null;
    }
    this.autoCaptureTriggered = false;
    this.flashEnabled = false;
    this.statusIndex = 0;
    this.ready = false;
    this.setStatus('NO_FACE_DETECTED');
    this.syncButtonState();
    this.stopCamera();
    try {
      await this.initialize();
    } finally {
      this.refreshing = false;
      if (this.refreshButton) this.refreshButton.disabled = false;
    }
  }
  async initialize() {
    dispatchCustomEvent(this, 'webCapBeforeInitialize', {
      component: 'verifone-face-capture'
    });
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera API not supported');
      }
      dispatchCustomEvent(this, 'webCapBeforeOpen', {
        component: 'verifone-face-capture'
      });
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: {
            ideal: 640
          },
          height: {
            ideal: 480
          }
        },
        audio: false
      });
      if (this.video) {
        this.video.srcObject = this.stream;
        await this.video.play();
      }
      this.ready = true;
      this.syncFlashButton();
      this.currentStatus = 'NO_FACE_DETECTED';
      this.syncButtonState();
      dispatchCustomEvent(this, 'webCapOpen', {
        component: 'verifone-face-capture'
      });
      dispatchCustomEvent(this, 'webCapInitialize', {
        component: 'verifone-face-capture',
        ready: true
      });
      console.log('[verifone-face-capture] initialized and camera is ready');
      this.setStatus('NO_FACE_DETECTED');
      this.startLivenessCycle();
    } catch (error) {
      this.ready = false;
      this.syncFlashButton();
      this.setStatus('NO_FACE_DETECTED');
      dispatchCustomEvent(this, 'webCapError', {
        message: 'Camera access denied or unavailable',
        error
      });
    }
  }
  stopCamera() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    if (this.video) {
      this.video.pause();
      this.video.srcObject = null;
    }
    this.flashEnabled = false;
    this.syncFlashButton();
    dispatchCustomEvent(this, 'webCapClose', {
      component: 'verifone-face-capture'
    });
  }
  async capture() {
    if (!this.ready || !this.video || this.capturing || this.captureTransitionTimer) {
      return;
    }
    if (this.button && this.button.disabled) {
      this.setStatus(this.currentStatus);
      return;
    }
    this.capturing = true;
    try {
      console.log('[verifone-face-capture] capture triggered for status:', this.currentStatus);
      dispatchCustomEvent(this, 'webCapBeforeCapture', {
        status: this.currentStatus
      });
      const width = this.video.videoWidth || 640;
      const height = this.video.videoHeight || 480;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) {
        dispatchCustomEvent(this, 'webCapError', {
          message: 'Capture failed: canvas context unavailable'
        });
        return;
      }
      context.drawImage(this.video, 0, 0, width, height);
      const blob = await new Promise(resolve => {
        canvas.toBlob(result => resolve(result), 'image/jpeg', 0.92);
      });
      if (!blob) {
        dispatchCustomEvent(this, 'webCapError', {
          message: 'Capture failed: image blob was empty'
        });
        return;
      }
      const payload = [{
        photo: blob,
        status: this.currentStatus
      }];
      this.captureTransitionTimer = window.setTimeout(() => {
        this.captureTransitionTimer = null;
        if (this.isConnected) {
          dispatchCustomEvent(this, 'webCapFaceCapture', payload);
        }
      }, 3000);
    } finally {
      this.capturing = false;
    }
  }
}
if (typeof customElements !== 'undefined' && !customElements.get('verifone-face-capture')) {
  customElements.define('verifone-face-capture', VerifoneFaceCapture);
}
export default VerifoneFaceCapture;